import { useAppSelector } from 'appState/store'
import CompletedCalls from 'assets/images/icons/CompletedCalls.svg?react'
import { CustomDropdown } from 'components/Menus/CustomDropdown'
import { getClaimCallSummaryPath, getClaimDetailPath } from 'features/claims/utils/claimsPathUtils'
import { useUserProfile } from 'features/settings/settingsHooks'
import { IUserRoleGuidEnum } from 'features/settings/settingsTypes'
import moment from 'moment'
import { useEffect, useRef, useState } from 'react'
import { FaArrowRight, FaCaretDown, FaCaretUp, FaPhoneAlt } from 'react-icons/fa'
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import { formatAMPM } from 'utils/date'
import { longTextTruncate } from 'utils/helper'

import { useCurrentUserCalls } from '../callsHooks'
import { CallTypeToJobTypeEnum, ICall } from '../types'
import { getTntIdForNavigation } from 'utils/tenantUrlUtils'

export const CurrentUserLiveCallsDisplay = () => {
  // global state
  const { user } = useAppSelector((state) => state.auth)
  const { data: profile } = useUserProfile(user?.id)
  const roleIds = profile?.roles.map((role) => role.roleId)
  const isClaimAccessPermission = roleIds?.includes(IUserRoleGuidEnum.claimAccessPermission)
  const tenants = profile?.tenants || []
  const [countUpdated, setCountUpdated] = useState(false)
  type DropdownStates = {
    live: boolean
    completed: boolean
    failed: boolean
  }

  const [dropdownStates, setDropdownStates] = useState<DropdownStates>({
    live: false,
    completed: false,
    failed: false,
  })

  const handleDropdownToggle = (dropdownName: keyof DropdownStates) => {
    setDropdownStates((prevState) => ({
      ...prevState,
      [dropdownName]: !prevState[dropdownName],
    }))
  }

  type DropdownNames = 'live' | 'completed' | 'failed'

  // Define dropdownRefs outside the return block
  const dropdownRefs: { [key in DropdownNames]: React.MutableRefObject<HTMLDivElement | null> } = {
    live: useRef(null),
    completed: useRef(null),
    failed: useRef(null),
  }

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      for (const dropdownName in dropdownRefs) {
        const currentDropdownRef = dropdownRefs[dropdownName as DropdownNames].current
        if (currentDropdownRef && !currentDropdownRef.contains(event.target as Node)) {
          setDropdownStates((prevState) => ({
            ...prevState,
            [dropdownName]: false,
          }))
        }
      }
    }

    const handleFocusOut = () => {
      for (const dropdownName in dropdownRefs) {
        const currentDropdownRef = dropdownRefs[dropdownName as DropdownNames].current
        if (currentDropdownRef) {
          setDropdownStates((prevState) => ({
            ...prevState,
            [dropdownName]: false,
          }))
        }
      }
    }

    document.addEventListener('mousedown', handleOutsideClick)
    document.addEventListener('focusout', handleFocusOut)

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick)
      document.removeEventListener('focusout', handleFocusOut)
    }
  }, [])

  // api
  const { data: pendingDispositionCallsResponse } =
    isClaimAccessPermission && tenants?.length > 0
      ? useCurrentUserCalls(user?.id, 'notDispositioned')
      : {
        data: {
          data: [],
        },
      }
  const { data: activeCalls } =
    isClaimAccessPermission && tenants?.length > 0
      ? useCurrentUserCalls(user?.id, 'live')
      : {
        data: {
          data: [],
        },
      }

  // utils
  const pendingDispositionCalls = pendingDispositionCallsResponse?.data?.filter((call) => !!call.jobCompleted) || []
  const groupCompletedCallsByDate = () => {
    const mappedGroups = pendingDispositionCalls.reduce((groups: any, call) => {
      const date = (call && call?.jobCompleted && moment(call.jobCompleted).local().format('YYYY-MM-DD')) || ''
      if (date && !groups[date]) {
        groups[date] = []
      }

      groups[date].push(call)
      return groups as ICall[]
    }, {})

    const sortCallsByTime = (calls: ICall[]) => {
      calls.sort((a, b) => b.jobCompleted!.localeCompare(a.jobCompleted!))
      return calls
    }
    const groupArrays = Object.keys(mappedGroups).map((date) => {
      return {
        date,
        calls: sortCallsByTime(mappedGroups[date]) as ICall[],
      }
    })

    const sortCallsByDate = groupArrays.sort((a, b) => b.date.localeCompare(a.date))
    return sortCallsByDate
  }

  const calls = activeCalls?.data || []
  const activeClaimStatusCalls = calls.filter((call) => call.jobType === CallTypeToJobTypeEnum.claimStatus)
  const groupActiveCallsByDate = () => {
    const mappedGroups = activeClaimStatusCalls.reduce((groups: any, call) => {
      const date = call && call?.jobQueued && moment(call.jobQueued).local().format('YYYY-MM-DD')
      if (date && !groups[date]) {
        groups[date] = []
      }

      if (date) {
        groups[date].push(call)
        return groups
      }
    }, {})

    const sortCallsByTime = (calls: ICall[]) => {
      calls.sort((a, b) => b.jobQueued!.localeCompare(a.jobQueued!))
      return calls
    }
    const groupArrays = Object.keys(mappedGroups).map((date) => {
      return {
        date,
        calls: sortCallsByTime(mappedGroups[date]) as ICall[],
      }
    })

    const sortCallsByDate = groupArrays.sort((a, b) => b.date.localeCompare(a.date))
    return sortCallsByDate
  }
  let callListUpdate = JSON.parse(localStorage.getItem('completedcalls')!)

  const ClearNotification = () => {
    if (callListUpdate != null && callListUpdate.length > 0) {
      localStorage.removeItem('completedcalls')
      setCountUpdated(false)
    }
  }

  useEffect(() => {
    if (callListUpdate) {
      callListUpdate = JSON.parse(localStorage.getItem('completedcalls')!)
      setCountUpdated(true)
    }
  }, [pendingDispositionCalls])

  return (
    <>
      {(pendingDispositionCalls.length > 0 || activeClaimStatusCalls.length > 0) && (
        <Div>
          {activeClaimStatusCalls && activeClaimStatusCalls.length > 0 && (
            <CustomDropdown
              toggler={
                <div
                  ref={dropdownRefs.live}
                  className={`d-flex align-items-center gap-2 live-call ${dropdownStates.live ? 'expanded' : ''}`}
                  onClick={() => handleDropdownToggle('live')}
                >
                  <FaPhoneAlt />
                  {activeClaimStatusCalls.length}
                  {dropdownStates.live ? <FaCaretUp /> : <FaCaretDown />}
                </div>
              }
              tooltipText="My live “fetch” (IVR) calls"
              title={<div className="live">Live Calls</div>}
              header={'Select a call to view the Claim Details page'}
            >
              <div className="link-item">
                {groupActiveCallsByDate().map(({ date, calls }, index) => {
                  return (
                    <div key={index} className="date-container">
                      <p className="date">{moment(date).local().format('MM/DD/YYYY')}</p>
                      {calls.map((call, i) => {
                        const name = `${call.oaiClaim?.patientFirstName} ${call.oaiClaim?.patientLastName}`
                        return (
                          <div key={i}>
                            <Link to={getClaimDetailPath(getTntIdForNavigation(), call.claimId)} className={` call-detail-container`}>
                              <FaArrowRight className="arrow-right" />
                              <div className="calls-container">
                                <p className="time">{formatAMPM(call?.jobQueued)}</p>
                                <p className="payer">
                                  {call?.preferredPayerName
                                    ? longTextTruncate(call?.preferredPayerName, 22)
                                    : call?.oaiClaim ? longTextTruncate(call?.oaiClaim?.insurerName, 22) : '---'}
                                </p>
                                <p className="patient">Patient: {longTextTruncate(name, 18)}</p>
                                <p className="account">Account: {longTextTruncate(call.oaiClaim?.visitId || '', 18)}</p>
                              </div>
                            </Link>
                          </div>
                        )
                      })}
                    </div>
                  )
                })}
              </div>
            </CustomDropdown>
          )}

          {pendingDispositionCalls.length > 0 && (
            <CustomDropdown
              toggler={
                <div
                  ref={dropdownRefs.completed}
                  className={`d-flex align-items-center gap-2 completed-call ${dropdownStates.completed ? 'expanded' : ''
                    }`}
                  onClick={() => handleDropdownToggle('completed')}
                >
                  <CompletedCalls className="callcompleted-icon" />
                  {countUpdated ? <span className="indicator"></span> : ''}
                  {pendingDispositionCalls.length}
                  {dropdownStates.completed ? <FaCaretUp /> : <FaCaretDown />}
                </div>
              }
              onHide={() => ClearNotification()}
              tooltipText="My completed calls"
              title={<div className="completed">Completed Calls</div>}
              header={'Select a call to view the call summary'}
            >
              <div className="link-item">
                {groupCompletedCallsByDate().map(({ date, calls }, index) => {
                  return (
                    <div key={index} className="date-container">
                      <p className="date">{moment(date).local().format('MM/DD/YYYY')}</p>
                      {calls.map((call, i) => {
                        let isActive = false
                        if (callListUpdate !== null) {
                          isActive = callListUpdate.includes(call.jobId)
                        }
                        const name = `${call.oaiClaim?.patientFirstName} ${call.oaiClaim?.patientLastName}`
                        return (
                          <div key={i}>
                            <Link
                              id={isActive ? 'active' : ''}
                              key={i + call.jobRequested}
                              to={getClaimCallSummaryPath(getTntIdForNavigation(), call.claimId, call.jbId)}
                              className={` call-detail-container`}
                            >
                              <FaArrowRight className="arrow-right" />
                              <div className="calls-container">
                                <p className="time">{formatAMPM(call.jobCompleted)}</p>
                                <p className="payer">
                                  {call?.preferredPayerName
                                    ? longTextTruncate(call?.preferredPayerName, 22)
                                    : call?.oaiClaim ? longTextTruncate(call?.oaiClaim?.insurerName, 22) : '---'}
                                </p>
                                <p className="patient">Patient: {longTextTruncate(name, 18)}</p>
                                <p className="account">Account: {longTextTruncate(call.oaiClaim?.visitId || '', 18)}</p>
                              </div>
                            </Link>
                          </div>
                        )
                      })}
                    </div>
                  )
                })}
              </div>
            </CustomDropdown>
          )}
        </Div>
      )}
    </>
  )
}

const Div = styled.div`
  display: flex;
  background-color: ${({ theme }) => theme.colors.primary.dark};
  border-radius: ${({ theme }) => theme.borderRadius.rounded};
  padding: ${({ theme }) => theme.util.pixelToRem(3.21)} 0.5rem;
  gap: 0.5rem;
  .custom-dropdown-title,
  .custom-dropdown-header {
    padding: 0 1rem;
  }
  .custom-dropdown-modal {
    background: ${({ theme }) => theme.colors.neutral.lightest};
    padding: 1rem 0 0 0;
    right: -0.5rem;
    top: 2.5rem;
    .custom-dropdown-content {
      padding-block: 0;
    }
  }

  .custom-dropdown-toggle:hover {
    background-color: ${({ theme }) => theme.colors.primary.neutral};
  }
  /* .custom-dropdown-toggle:active {
    background-color: ${({ theme }) => theme.colors.grey.lightest};
  } */
  .custom-dropdown-toggle.active {
    background-color: ${({ theme }) => theme.colors.primary.dark};
  }
  .custom-dropdown-toggle.active:hover {
    background-color: ${({ theme }) => theme.colors.primary.neutral};
  }

  .live {
    ${({ theme }) => theme.typography.s.medium};
    color: ${({ theme }) => theme.colors.success.neutral};
  }
  .completed {
    ${({ theme }) => theme.typography.s.medium};
    color: ${({ theme }) => theme.colors.tertiary.dark};
  }
  .failed {
    color: ${({ theme }) => theme.colors.error.dark};
  }

  .live-call {
    color: ${({ theme }) => theme.colors.success.light};
    ${({ theme }) => theme.typography.m.medium};
    &:hover,
    &:focus,
    &:active,
    &.active,
    &.expanded {
      color: ${({ theme }) => theme.colors.success.light};
    }
  }
  .completed-call {
    .callcompleted-icon {
      color: ${({ theme }) => theme.colors.tertiary.neutral};
    }
    color: ${({ theme }) => theme.colors.tertiary.neutral};
    ${({ theme }) => theme.typography.m.medium};
  }
  .link-item {
    padding: 0.5rem 0 0 0;
    .date-container {
      border-bottom: 1px solid ${({ theme }) => theme.colors.grey.lighter};
    }
    .date-container:last-child {
      border-bottom: none;
      padding-top: 0;
    }
    .date {
      ${({ theme }) => theme.typography.xs.medium};
      padding: 0.75rem 0 0.25rem 1rem;
    }

    .calls-container {
      display: flex;
      flex-direction: column;
      /* margin-bottom: 0.75rem; */
      .time {
        ${({ theme }) => theme.typography.xs.normal};
        color: ${({ theme }) => theme.colors.grey.neutral};
        padding-bottom: 0.25rem;
      }
      .payer {
        ${({ theme }) => theme.typography.s.medium};
        color: ${({ theme }) => theme.colors.neutral.neutral};
        padding-bottom: 0.25rem;
      }
      .patient,
      .account {
        ${({ theme }) => theme.typography.s.normal};
        color: ${({ theme }) => theme.colors.neutral.neutral};
        padding-bottom: 0.25rem;
      }
      .account {
        padding-bottom: 0;
      }
    }
    .call-detail-container {
      display: flex;
      align-items: center;
      gap: 4px;
      padding: 0.5rem 1rem;
      .arrow-right {
        display: none;
        position: absolute;
        right: 1rem;
        color: ${({ theme }) => theme.colors.neutral.neutral};
      }
    }
    .call-detail-container:hover {
      background-color: ${({ theme }) => theme.colors.grey.lightest};
      .arrow-right {
        display: block;
      }
    }
  }
`
